/**
 * Client for the backend's personal API key endpoints (/developer/api-keys).
 * Authenticated with the signed-in user's Firebase token.
 */
import { auth } from './firebase';

// REACT_APP_BACKEND_URL points a local build at a local backend (see .claude/launch.json).
export const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'https://call-for-me-prod-3055119120.us-central1.run.app';
export const MCP_URL = 'https://mcp.callout.jp/mcp';

export interface ApiKey {
  id: string;
  name: string;
  preview: string;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export interface CreatedApiKey extends ApiKey {
  /** The full secret. Only returned once, at creation. */
  key: string;
}

async function developerFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('Please sign in again.');
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function listApiKeys(): Promise<ApiKey[]> {
  const data = await developerFetch<{ keys: ApiKey[] }>('/developer/api-keys');
  return data.keys;
}

export function createApiKey(name: string): Promise<CreatedApiKey> {
  return developerFetch<CreatedApiKey>('/developer/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function revokeApiKey(id: string): Promise<void> {
  await developerFetch(`/developer/api-keys/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
